import { useState } from 'react';
import { isEqual } from 'lodash';
import { InvoicesVariantRequestDto } from '@tiendanube/common';
import { ProductVariantAgentTool } from '@tiendanube/common/src/domains/ai/interfaces/AgentTypes';
import { CATALOG_ATTRIBUTES_UNCASE } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useAiField, useModal } from 'commons/hooks';
import { mapVariantToContext } from 'commons/services/AgentChatService';
import { AI_FIELDS } from 'commons/services/AgentChatService/AgentChatActionBusTypes';
import { useGetLanguage } from 'domains/Auth/hooks';
import useLocations from 'domains/Shipping/Locations/hooks/useLocations';
import {
  AttributesModal,
  CardVariants,
  VariantDetailsModal,
  VariantImageModal,
} from './components';
import { useBrokenVariantsAlert } from './hooks';
import {
  Attributes,
  Variant,
  OnChangeToAllVariants,
  OnChangeToAllMetafieldsVariants,
  Attribute,
  OnChangeAttributes,
  RequiredAiProperties,
} from './types';
import { removeTmpId, addTmpId } from './utils';
import { ImageGalleryState } from '../ImageGallery/ImageGallery';

export interface VariantsProps {
  variants: Variant[];
  attributes: Attributes;
  productImages: ImageGalleryState[];
  isDigital: boolean;
  defaultImage?: string;
  isEdit?: boolean;
  productId: string;
  requiredAiProperties: RequiredAiProperties;
  onChange: (i: number, v: Partial<Variant>) => void;
  onChangeAttributes: OnChangeAttributes;
  onChangeToAllVariants: OnChangeToAllVariants;
  onChangeToAllMetafieldsVariants: OnChangeToAllMetafieldsVariants;
  onChangeInvoice: (i: number, data: InvoicesVariantRequestDto) => void;
}

function Variants({
  variants,
  attributes,
  productImages,
  isDigital,
  defaultImage = '',
  isEdit,
  productId,
  requiredAiProperties,
  onChange,
  onChangeAttributes,
  onChangeToAllVariants,
  onChangeToAllMetafieldsVariants,
  onChangeInvoice,
}: VariantsProps): JSX.Element {
  const language = useGetLanguage();
  const [hasAttributesUncase] = useHasTags([CATALOG_ATTRIBUTES_UNCASE]);
  const [isShowAttributes, openAttributes, closeAttributes] = useModal();
  const [isShowVariantDetails, openVariantDetails, closeVariantDetails] =
    useModal();
  const [isShowVariantImage, openVariantImage, closeVariantImage] = useModal();
  const [variantSelected, setVariantSelected] = useState(0);
  const brokenVariantsAlert = useBrokenVariantsAlert(variants, attributes);

  const { locationsMainId } = useLocations();

  useAiField<ProductVariantAgentTool[]>({
    componentId: AI_FIELDS.PRODUCT_VARIANTS,
    getValue: () =>
      variants.map((variant, index) => ({
        index,
        fields: mapVariantToContext(variant),
      })),
  });

  const disabledHideVariant =
    variants.filter((variant) => !variant.visible).length ===
    variants.length - 1;

  const handleOnClickVariant = (variantIndex: number) => {
    setVariantSelected(variantIndex);
    openVariantDetails();
  };
  const handleOnClickImage = (variantIndex: number) => {
    setVariantSelected(variantIndex);
    openVariantImage();
  };

  // eslint-disable-next-line max-statements
  const handleOnChangeAttributeValues = (
    newAttribute: Attribute,
    oldAttribute?: Attribute,
  ) => {
    // Get the attribute names in the current language
    let newAttributeName = newAttribute.name[language] ?? '';
    const oldAttributeName = oldAttribute?.name[language] ?? '';

    // If the attribute name is the same (ignoring case), update the attribute name
    // and update the attribute object
    if (
      hasAttributesUncase &&
      newAttributeName.toLowerCase().trim() ===
        oldAttributeName.toLowerCase().trim()
    ) {
      newAttributeName = oldAttributeName;
      newAttribute = {
        ...newAttribute,
        name: { ...newAttribute.name, [language]: oldAttributeName },
      };
    }

    // If the attribute name has changed, update the attributes object
    if (oldAttribute && newAttributeName !== oldAttributeName) {
      const newAttributes = { ...attributes };
      delete newAttributes[oldAttributeName];
      newAttributes[newAttributeName] = newAttribute;
      return onChangeAttributes(newAttributes);
    }

    // Create a copy of variants to track changes
    const clonedVariants = [...variants];
    let hasChanges = false;

    // Iterate through each new value in the attribute
    newAttribute.values.forEach((newValue) => {
      // Find the corresponding old value using tmpId for comparison
      const oldValue =
        oldAttribute?.values.find(
          (oldValue) => oldValue.tmpId === newValue.tmpId,
        ) ?? {};

      // If the value has changed, update all variants that use this value
      if (!isEqual(newValue, oldValue)) {
        // Remove temporary IDs for comparison
        const newValueCleaned = removeTmpId(newValue);
        const oldValueCleaned = removeTmpId(oldValue);

        // Update all variants that contain the old value
        clonedVariants.forEach((variant, index) => {
          variant.values.forEach((variantValue) => {
            if (isEqual(variantValue, oldValueCleaned)) {
              // Create a new array of values for the variant
              const newValues = [...variant.values];
              // Replace the old value with the new one
              newValues.forEach((v, index) => {
                if (isEqual(v, variantValue)) {
                  newValues[index] = { ...newValueCleaned };
                }
              });
              // Update the variant with new values and regenerate its name
              const updatedVariant = {
                ...variant,
                values: newValues,
                name: newValues.map((v) => v[language]).join(' '),
                hasValuesChanges: true,
              };
              clonedVariants[index] = updatedVariant;
              hasChanges = true;
            }
          });
        });
      }
    });

    // If any variants were updated, trigger the onChange callback for each
    if (hasChanges) {
      clonedVariants.forEach((variant, index) => {
        onChange(index, variant);
      });
    }

    // Update the attributes with the new attribute values
    onChangeAttributes(
      {
        ...attributes,
        [newAttributeName]: newAttribute,
      },
      clonedVariants,
    );
  };

  const attributesWithTmpId = addTmpId(attributes);

  return (
    <>
      <CardVariants
        onClickVariant={handleOnClickVariant}
        onClickImage={handleOnClickImage}
        onClickAttributes={openAttributes}
        onChange={onChange}
        variants={variants.map((variant) => ({
          stock: variant.stock,
          imageUrl: variant.imageUrl || defaultImage,
          id: variant.id,
          name: variant.name,
          isInfinite: variant.isInfinite,
          productId: variant.productId,
          price: variant.price,
          weight: variant.weight,
          depth: variant.depth,
          width: variant.width,
          height: variant.height,
          locationId: variant.locationId || locationsMainId,
          inventoryLevels: variant.inventory_levels,
          visible: variant.visible ?? true,
        }))}
        attributes={attributes}
        onChangeAttributes={onChangeAttributes}
        isEdit={isEdit}
        onChangeToAllVariants={onChangeToAllVariants}
        brokenVariantsAlert={brokenVariantsAlert}
        isDigital={isDigital}
      />
      <AttributesModal
        attributes={attributesWithTmpId}
        isShow={isShowAttributes}
        onChange={onChangeAttributes}
        onChangeAttributeValues={handleOnChangeAttributeValues}
        onClose={closeAttributes}
      />
      <VariantDetailsModal
        productId={productId}
        isShow={isShowVariantDetails}
        onClose={closeVariantDetails}
        variants={variants}
        variantSelected={variantSelected}
        isDigital={isDigital}
        disabledHideVariant={disabledHideVariant}
        requiredAiProperties={requiredAiProperties}
        productImages={productImages}
        onChangeVariantSelected={setVariantSelected}
        onChangeVariant={onChange}
        onChangeToAllVariants={onChangeToAllVariants}
        onChangeToAllMetafieldsVariants={onChangeToAllMetafieldsVariants}
        onChangeInvoice={onChangeInvoice}
      />
      <VariantImageModal
        isShow={isShowVariantImage}
        onClose={closeVariantImage}
        images={productImages}
        variants={variants}
        variantSelected={variantSelected}
        onChangeVariantSelected={setVariantSelected}
        onChangeVariant={onChange}
      />
    </>
  );
}

export default Variants;
